import { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import Map, { MapRef } from 'react-map-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useDistanceCalculations } from './map/hooks/useDistanceCalculations'
import { useGeoSources } from './map/hooks/useGeoSources'
import { useGridCell } from './map/hooks/useGridCell'
import { useDrawSetup } from './map/hooks/useDrawSetup'
import { useMapNativeEvents } from './map/hooks/useMapNativeEvents'
import { useMapStyleAndIcons } from './map/hooks/useMapStyleAndIcons'
import { useZoneViewport } from './map/hooks/useZoneViewport'
import { useEditSaveCancel } from './map/hooks/useEditSaveCancel'
import { useMapClickHandler } from './map/hooks/useMapClickHandler'
import { useMapLifecycle } from './map/hooks/useMapLifecycle'
import { useEditModeEffects } from './map/hooks/useEditModeEffects'
import { generateGridCell } from '../utils/mapHelpers'
import { ZonesLayers } from './map/ZonesLayers'
import { MapTypeToggle } from './map/MapTypeToggle'
import {
  MAPBOX_ACCESS_TOKEN,
  MAP_CENTER_LNG,
  MAP_CENTER_LAT,
  MAP_DEFAULT_ZOOM,
} from '../../../config/env'
import type { MapViewProps, Zone } from '../../zones/types'

// Default map center coordinates (Mapbox uses [lng, lat])
const CARIBBEAN_CENTER: [number, number] = [MAP_CENTER_LNG, MAP_CENTER_LAT]

// Extend window for global functions
declare global {
  interface Window {
    _drawControlSaveEdit?: () => Promise<{
      coordinates: number[][]
      area: number
    } | null>
    _drawControlCancelEdit?: () => void
    _drawControlGetCreatePolygon?: () => Promise<{
      coordinates: number[][]
      area: number
    } | null>
    _drawControlGetGridCell?: () => {
      coordinates: number[][]
      area: number
    } | null
  }
}

export default function MapView({
  drawingMode = 'draw',
  gridSize = 0.1,
  gridCellPosition,
  onGridCellPositionChange,
  hideDrawControls = false,
  selectedZone: externalSelectedZone,
  setSelectedZone,
  onDistanceCalculated,
  onAreaCalculated,
  onAreaCalculatingChange,
  onDistanceCalculatingChange,
  onMapReady,
  editingZoneId,
  hasPolygon = false,
  onPolygonChange,
  editMode = 'polygon',
  onEditModeChange,
  mapType = 'street',
  onMapTypeChange,
  onError,
  blockMapInteractionsRef,
}: Readonly<MapViewProps>) {
  const mapRef = useRef<MapRef>(null)
  const drawRef = useRef<MapboxDraw | null>(null)
  const { zones: mapZones } = useAppSelector(state => state.zones)
  const dispatch = useAppDispatch()

  const [dragPanEnabled, setDragPanEnabled] = useState(true)

  // Memoize optional props to reduce cognitive complexity of hook calls
  const distanceConfig = useMemo(
    () => ({
      ...(onDistanceCalculated && { onDistanceCalculated }),
      ...(onDistanceCalculatingChange && { onDistanceCalculatingChange }),
      ...(onError && { onError }),
    }),
    [onDistanceCalculated, onDistanceCalculatingChange, onError]
  )

  const { lastCalculatedDistanceRef } = useDistanceCalculations(distanceConfig)

  const zonesSourceId = 'zones-source'
  const zonesLayerId = 'zones-layer'
  const zonesOutlineLayerId = 'zones-outline-layer'
  const gridCellSourceId = 'grid-cell-source'
  const gridCellLayerId = 'grid-cell-layer'
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const justFinishedDragging = useRef(false)
  const pendingGridPlacementRef = useRef<[number, number] | null>(null)

  const normalizedEditingZoneId = editingZoneId ?? null
  const normalizedEditMode = editMode ?? null
  const normalizedGridCellPosition = gridCellPosition ?? null

  const getZoneById = useCallback(
    (id: string): Zone | undefined => mapZones.find((z: Zone) => z.id === id),
    [mapZones]
  )

  const handleSetSelectedZone = useCallback(
    (zone: Zone | null) => {
      setSelectedZone?.(zone)
    },
    [setSelectedZone]
  )

  // eslint-disable-next-line no-console
  console.log('[MapView] Props', {
    drawingMode,
    hideDrawControls,
    editingZoneId,
  })
  const { isDrawReady } = useDrawSetup({
    mapRef,
    drawRef,
    hideDrawControls,
    drawingMode,
    ...(onAreaCalculated && { onAreaCalculated }),
    ...(onAreaCalculatingChange && { onAreaCalculatingChange }),
    ...(onDistanceCalculatingChange && { onDistanceCalculatingChange }),
    editingZoneId: normalizedEditingZoneId,
    dispatch,
    ...(onAreaCalculated && { onAreaCalculatedExternal: onAreaCalculated }),
    hasPolygon,
    ...(onPolygonChange && { onPolygonChange }),
  })

  const { zonesGeoJSON } = useGeoSources({
    mapZones,
    editingZoneId: normalizedEditingZoneId,
  })

  const mapClickOptions = useMemo(
    () => ({
      mapRef,
      drawRef,
      popupRef,
      justFinishedDraggingRef: justFinishedDragging,
      pendingGridPlacementRef,
      drawingMode,
      editingZoneId: normalizedEditingZoneId,
      editMode: normalizedEditMode,
      gridSize,
      zonesLayerId,
      gridCellLayerId,
      getZoneById,
      handleSetSelectedZone,
      ...(onGridCellPositionChange && { onGridCellPositionChange }),
      ...(onEditModeChange && { onEditModeChange }),
      ...(onError && { onError }),
      ...(blockMapInteractionsRef && { blockMapInteractionsRef }),
    }),
    [
      drawingMode,
      normalizedEditingZoneId,
      normalizedEditMode,
      gridSize,
      getZoneById,
      handleSetSelectedZone,
      onGridCellPositionChange,
      onEditModeChange,
      onError,
      blockMapInteractionsRef,
    ]
  )

  const handleMapClick = useMapClickHandler(mapClickOptions)

  useEffect(() => {
    if (!externalSelectedZone && popupRef.current) {
      popupRef.current.remove()
      popupRef.current = null
    }
  }, [externalSelectedZone])

  const nativeEventOptions = useMemo(
    () => ({
      mapRef,
      drawRef,
      drawingMode,
      editingZoneId: normalizedEditingZoneId,
      editMode,
      gridCellLayerId,
      gridCellSourceId,
      gridSize,
      hasPolygon,
      gridCellPosition: normalizedGridCellPosition,
      generateGridCell,
      setDragPanEnabled,
      dragPanEnabled,
      getZoneById,
      handleSetSelectedZone,
      zonesLayerId,
      justFinishedDraggingRef: justFinishedDragging,
      ...(onGridCellPositionChange && { onGridCellPositionChange }),
      ...(onError && { onError }),
      ...(blockMapInteractionsRef && { blockMapInteractionsRef }),
    }),
    [
      drawingMode,
      normalizedEditingZoneId,
      editMode,
      gridSize,
      hasPolygon,
      normalizedGridCellPosition,
      dragPanEnabled,
      getZoneById,
      handleSetSelectedZone,
      onGridCellPositionChange,
      onError,
      blockMapInteractionsRef,
    ]
  )

  useMapNativeEvents(nativeEventOptions)

  const mapStyleConfig = useMemo(
    () => ({
      mapRef,
      mapType,
      ...(onMapReady && { onMapReady }),
    }),
    [mapType, onMapReady]
  )

  const { mapStyle, handleMapLoad } = useMapStyleAndIcons(mapStyleConfig)

  useZoneViewport({
    mapRef,
    activeSelectedZone: externalSelectedZone ?? null,
    editingZoneId: normalizedEditingZoneId,
    getZoneById,
  })

  useEditModeEffects({
    mapRef,
    drawRef,
    popupRef,
    editingZoneId: normalizedEditingZoneId,
    editMode: normalizedEditMode,
    getZoneById,
    ...(onGridCellPositionChange && { onGridCellPositionChange }),
    gridCellPosition: normalizedGridCellPosition,
    zonesSourceId,
    zonesLayerId,
    zonesOutlineLayerId,
    zonesGeoJSON,
    setDragPanEnabled,
    isDrawReady,
  })

  useMapLifecycle({ mapRef, drawRef })

  useEditSaveCancel({
    editingZoneId: normalizedEditingZoneId,
    editMode: normalizedEditMode,
    gridCellPosition: normalizedGridCellPosition,
    gridSize,
    generateGridCell,
    getZoneById,
    drawRef,
    lastCalculatedDistanceRef,
    setDragPanEnabled,
  })

  useGridCell({
    mapRef,
    drawingMode,
    editingZoneId: normalizedEditingZoneId,
    editMode,
    gridSize,
    gridCellPosition: normalizedGridCellPosition,
    generateGridCell,
    gridCellSourceId,
    gridCellLayerId,
    ...(onAreaCalculated && { onAreaCalculated }),
    ...(onAreaCalculatingChange && { onAreaCalculatingChange }),
    lastCalculatedDistanceRef,
  })

  const interactiveLayerIds =
    drawingMode === 'grid' ? [zonesLayerId, gridCellLayerId] : [zonesLayerId]

  return (
    <div className='relative h-full w-full'>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        initialViewState={{
          longitude: CARIBBEAN_CENTER[0],
          latitude: CARIBBEAN_CENTER[1],
          zoom: MAP_DEFAULT_ZOOM,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        projection={{ name: 'globe' }}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        interactiveLayerIds={interactiveLayerIds}
        dragPan={dragPanEnabled}
      >
        <ZonesLayers
          zonesSourceId={zonesSourceId}
          zonesLayerId={zonesLayerId}
          zonesOutlineLayerId={zonesOutlineLayerId}
          zonesGeoJSON={zonesGeoJSON}
          editingZoneId={normalizedEditingZoneId}
        />
      </Map>

      <MapTypeToggle
        mapType={mapType}
        {...(onMapTypeChange && { onMapTypeChange })}
      />
    </div>
  )
}
